import { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Terminal } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg, useRefresh } from "../lib/api";
import { fmtDate } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { DropZone } from "../components/DropZone";
import { Chips } from "../components/Filters";

const useUploads = () => useQuery({ queryKey: ["uploads"], queryFn: async () => (await axios.get(`${API}/uploads`)).data });

const Panel = ({ title, children }) => (
  <section className="rise rounded-lg border border-slate-800 bg-[#131b29]/80 p-6">
    <h2 className="font-display mb-4 text-xl font-semibold uppercase tracking-wide">{title}</h2>
    {children}
  </section>
);

const downloadScript = async () => {
  const text = (await (await fetch("/Import-SapStock.ps1")).text()).replace("__APP_URL__", window.location.origin);
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "Import-SapStock.ps1" });
  a.click();
  URL.revokeObjectURL(url);
};

const ScriptPanel = () => (
  <Panel title="Automatic SAP upload from your server">
    <p className="mb-4 max-w-3xl text-sm text-slate-400">
      A PowerShell script that picks up the newest SAP stock file from your server folder and sends it here, then logs the result.
      Edit the source folder at the top of the script, then schedule it with Windows Task Scheduler (instructions inside the script).
      The script must send your secret upload key; store it on the server as the SAP_UPLOAD_KEY environment variable. Files without the right key are refused.
    </p>
    <button data-testid="download-ps-script-btn" onClick={downloadScript} className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-100 transition-colors hover:bg-slate-800">
      <Terminal className="h-4 w-4 text-sky-400" /> Download PowerShell script
    </button>
  </Panel>
);

export default function Uploads() {
  const refresh = useRefresh();
  const history = useUploads();
  const [busy, setBusy] = useState("");
  const [unit, setUnit] = useState("PALLETS");
  const send = async (kind, file) => {
    setBusy(kind);
    const fd = new FormData();
    fd.append("file", file);
    if (kind === "sap") fd.append("unit", unit.toLowerCase());
    try {
      const { data } = await axios.post(`${API}/uploads/${kind}`, fd);
      toast.success(kind === "sap" ? `SAP stock loaded: ${data.codes} codes, ${data.matched} linked` : `Forecast loaded: ${data.rows} FC lines for ${data.week_label}`);
      await Promise.all([refresh(), history.refetch()]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid="uploads-page">
      <PageHeader kicker="Weekly files" title="Uploads" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Amazon forecast file">
          <p className="mb-4 text-sm text-slate-400">The weekly 13-week forecast from Amazon SPOC (the "Forecast breakdown" sheet). Replaces the current forecast; your stock overrides are kept.</p>
          <DropZone testId="forecast-dropzone" busy={busy === "forecast"} onFile={(f) => send("forecast", f)} hint=".xlsx or .csv" />
        </Panel>
        <Panel title="SAP stock file">
          <p className="mb-4 text-sm text-slate-400">Needs a "SAP Code" (or Material) column and a "Free Stock" column. Optional "Pallets Sold Last Week" column fills Consumption WK-1. Products with our own stock holding keep it.</p>
          <div className="mb-4 flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Free stock is in</span>
            <Chips options={["PALLETS", "UNITS"]} value={unit} onChange={setUnit} testPrefix="sap-unit" />
          </div>
          <DropZone testId="sap-dropzone" busy={busy === "sap"} onFile={(f) => send("sap", f)} hint=".xlsx, .csv or .txt" />
        </Panel>
      </div>
      <div className="mt-6"><ScriptPanel /></div>
      <div className="mt-6" />
      <Panel title="Upload history">
        <div className="overflow-x-auto">
          <table className="data-table w-full text-sm" data-testid="upload-history">
            <thead><tr><th>When</th><th>Type</th><th>File</th><th>Result</th></tr></thead>
            <tbody>
              {(history.data || []).map((u) => (
                <tr key={u.id} data-testid={`upload-${u.id}`}>
                  <td className="num text-xs text-slate-400">{fmtDate(u.uploaded_at)}</td>
                  <td className="font-mono text-[11px] uppercase text-sky-300">{u.kind}</td>
                  <td className="max-w-[320px] truncate text-xs">{u.filename}</td>
                  <td className="text-xs text-slate-400">{u.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
